import PropTypes from 'prop-types';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { useEffect, useState, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import axiosInstance from 'src/utils/axios';

/**
 * RHFAutocompleteAsync - Autocomplete with API data fetching
 * Fetches options from an API endpoint (can be static string or function).
 *
 * FIX: When apiEndpoint is a function (cascading dropdown), pass the parent field
 * name via `dependsOn` prop so we subscribe ONLY to that one field — not every
 * field in the form.
 *
 * The old implementation used `useWatch()` with no name, which subscribes to ALL
 * form fields. When block fetched its options and called setOptions(), that state
 * update caused a re-render → all watched form values changed → JSON.stringify()
 * produced a new string → resolvedEndpoint changed → useEffect fired again →
 * infinite request loop.
 */
export default function RHFAutocompleteAsync({
    name,
    label,
    apiEndpoint,
    optionLabel = 'name',
    optionValue = '_id',
    placeholder,
    dependsOn,   // name of the parent field this field cascades from
    mapToId = true, // Whether to map to ID or store the whole object
    disabled: fieldDisabled, // Extract disabled to prevent cascading clear in edit mode
    showSelectAll,
    ...other
}) {
    const { control, setValue, formState: { dirtyFields } } = useFormContext();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef(null);

    // Resolve disabled: can be a boolean or a function
    const isDisabled = typeof fieldDisabled === 'function' ? fieldDisabled(other) : !!fieldDisabled;

    // Watch ONLY the parent field — prevents re-triggering on every form state change.
    // '__none__' is a field that is never set, so useWatch returns undefined without
    // subscribing to anything meaningful when dependsOn is not provided.
    const parentValue = useWatch({
        control,
        name: dependsOn || '__none__',
        disabled: typeof apiEndpoint !== 'function' || !dependsOn,
    });

    const prevParentRef = useRef(parentValue);

    useEffect(() => {
        // Automatically clear this field if the field it depends on changes.
        // We skip clearing entirely if the field is disabled (edit-mode locked fields
        // display existing values from the API; clearing would blank them out).
        // We only clear if:
        // 1. The parent value becomes empty/null (meaning no parent is selected)
        // 2. The parent field is dirty (meaning the user manually changed it in this session)
        if (isDisabled) {
            prevParentRef.current = parentValue;
            return;
        }
        const isParentEmpty = !parentValue || (Array.isArray(parentValue) && parentValue.length === 0);
        const isParentDirty = dependsOn && !!dirtyFields[dependsOn];

        if (dependsOn && prevParentRef.current !== undefined && prevParentRef.current !== parentValue) {
            if (isParentEmpty || isParentDirty) {
                setValue(name, other.multiple ? [] : null);
            }
        }
        prevParentRef.current = parentValue;
    }, [parentValue, dependsOn, name, setValue, other.multiple, dirtyFields, isDisabled]);

    // Resolve endpoint: for cascading fields, pass only {[dependsOn]: parentValue}
    // so the endpoint function doesn't see stale/unrelated form values.
    const resolvedEndpoint = (() => {
        if (typeof apiEndpoint !== 'function') return apiEndpoint;
        return apiEndpoint({ [dependsOn]: parentValue });
    })();

    useEffect(() => {
        // Cancel any previous in-flight request
        if (abortRef.current) abortRef.current.abort();

        // If disabled, don't fetch — existing values are injected via displayOptions fallback below
        if (isDisabled) {
            setOptions([]);
            return undefined;
        }

        if (!resolvedEndpoint) {
            setOptions([]);
            return undefined;
        }

        const controller = new AbortController();
        abortRef.current = controller;

        const fetchOptions = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(resolvedEndpoint, {
                    signal: controller.signal,
                });
                const data = response.data?.data || response.data || [];

                const transformed = data.map((item) => {
                    let rawLabel = typeof optionLabel === 'function' ? optionLabel(item) : item[optionLabel];
                    if (rawLabel === undefined || rawLabel === null || rawLabel === '') {
                        rawLabel = item.name || item.label || item.title ||
                            (item.firstName ? `${item.firstName} ${item.lastName || ''}`.trim() :
                                item.country_name || item.state_name || item.city_name || item.block_name || item.email);
                    } else if (typeof rawLabel === 'object') {
                        rawLabel = rawLabel.name || rawLabel.label || rawLabel.title ||
                            (rawLabel.firstName ? `${rawLabel.firstName} ${rawLabel.lastName || ''}`.trim() :
                                rawLabel.country_name || rawLabel.state_name || rawLabel.city_name || rawLabel.block_name || 'Object');
                    }

                    return {
                        label: String(rawLabel || item.label || ''),
                        value: item[optionValue] || item._id || item.id,
                        ...item,
                    };
                });

                setOptions(transformed);
            } catch (error) {
                if (
                    error.name === 'CanceledError' || 
                    error.name === 'AbortError' || 
                    error.message === 'canceled' ||
                    (axiosInstance.isCancel && axiosInstance.isCancel(error))
                ) {
                    return;
                }
                console.error('Error fetching autocomplete options:', error.message || error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedEndpoint]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                const currentValue = field.value;
                let displayOptions = [...options];

                // If showSelectAll and multiple, prepend Select All option
                if (showSelectAll && other.multiple && options.length > 0) {
                    displayOptions = [{ label: 'Select All', value: 'all' }, ...displayOptions];
                }

                // If current value is an object and not in options, add it (edit mode)
                if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
                    const valueId = currentValue._id || currentValue.id;
                    const existsInOptions = displayOptions.some(opt => {
                        const optId = opt._id || opt.id || opt.value;
                        return optId === valueId;
                    });
                    if (!existsInOptions && valueId) {
                        displayOptions = [currentValue, ...displayOptions];
                    }
                }

                // For multiple values (arrays)
                if (Array.isArray(currentValue) && currentValue.length > 0) {
                    currentValue.forEach(val => {
                        if (val && typeof val === 'object') {
                            const valueId = val._id || val.id;
                            const existsInOptions = displayOptions.some(opt => {
                                const optId = opt._id || opt.id || opt.value;
                                return optId === valueId;
                            });
                            if (!existsInOptions && valueId) {
                                displayOptions = [val, ...displayOptions];
                            }
                        }
                    });
                }

                return (
                    <Autocomplete
                        {...field}
                        disabled={isDisabled}
                        options={displayOptions}
                        loading={loading}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (!option) return '';
                            if (typeof option.label === 'string') return option.label;

                            let rawLabel = typeof optionLabel === 'function' ? optionLabel(option) : option[optionLabel];
                            if (rawLabel === undefined || rawLabel === null || rawLabel === '') {
                                rawLabel = option.name || option.label || option.title ||
                                    (option.firstName ? `${option.firstName} ${option.lastName || ''}`.trim() :
                                        option.country_name || option.state_name || option.city_name || option.block_name || option.email);
                            } else if (typeof rawLabel === 'object') {
                                rawLabel = rawLabel.name || rawLabel.label || rawLabel.title ||
                                    (rawLabel.firstName ? `${rawLabel.firstName} ${rawLabel.lastName || ''}`.trim() :
                                        rawLabel.country_name || rawLabel.state_name || rawLabel.city_name || rawLabel.block_name || 'Object');
                            }
                            return String(rawLabel || '');
                        }}
                        isOptionEqualToValue={(option, value) => {
                            const optVal = option.value || option._id || option.id;
                            const fieldVal = value?.value || value?._id || value?.id || value;
                            return optVal === fieldVal;
                        }}
                        onChange={(event, newValue) => {
                            if (showSelectAll && other.multiple) {
                                const selectedAll = newValue.find(item => item && (item.value === 'all' || item._id === 'all' || item.id === 'all'));

                                if (selectedAll) {
                                    const currentRealValues = (field.value || []).filter(v => {
                                        const vId = typeof v === 'object' ? v?.value || v?._id || v?.id : v;
                                        return vId !== 'all';
                                    });
                                    const isAllSelected = currentRealValues.length === options.length;

                                    if (!isAllSelected) {
                                        const allVals = options.map(opt => mapToId ? (opt.value || opt._id || opt.id || opt) : opt);
                                        field.onChange(allVals);
                                    } else {
                                        field.onChange([]);
                                    }
                                    return;
                                }
                            }

                            if (!newValue) {
                                field.onChange(null);
                            } else if (Array.isArray(newValue)) {
                                field.onChange(mapToId ? newValue.map(v => v.value || v._id || v.id || v) : newValue);
                            } else {
                                field.onChange(mapToId ? (newValue.value || newValue._id || newValue.id || newValue) : newValue);
                            }
                        }}
                        value={(() => {
                            const v = field.value;
                            if (other.multiple) {
                                const valArray = Array.isArray(v) ? v : [];
                                return valArray.map(val => {
                                    if (typeof val === 'object' && val !== null) return val;
                                    return displayOptions.find((opt) => (opt.value || opt._id || opt.id) === val) || val;
                                });
                            }
                            return typeof v === 'object' && v !== null
                                ? v
                                : displayOptions.find((opt) => (opt.value || opt._id || opt.id) === v) || null;
                        })()}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                placeholder={placeholder}
                                error={!!error}
                                helperText={typeof error?.message === 'object' ? JSON.stringify(error.message) : error?.message}
                                required={false} // Disable native browser tooltip, let RHF/Yup handle validation UI
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        {...other}
                    />
                );
            }}
        />
    );
}

RHFAutocompleteAsync.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    apiEndpoint: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    optionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    optionValue: PropTypes.string,
    placeholder: PropTypes.string,
    dependsOn: PropTypes.string,
    mapToId: PropTypes.bool,
    disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    showSelectAll: PropTypes.bool,
};
